import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { HardDrive, Search, Trash2, Image as ImageIcon, User, ChevronDown, ChevronUp, RefreshCw, CheckCircle } from 'lucide-react';
import { formatBytes } from '../../lib/imageCompression';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { AdminLayout } from '../../components/admin/AdminLayout';

interface UserStorage {
  user_id: string;
  email: string;
  first_name: string;
  company_name: string;
  total_files: number;
  total_bytes: number;
  last_upload_at: string | null;
}

interface ImageFile {
  path: string;
  size: number;
  uploaded_at: string;
  url: string;
}

export function AdminStorage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserStorage[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'files' | 'size' | 'date'>('size');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [userImages, setUserImages] = useState<ImageFile[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [totalStorage, setTotalStorage] = useState(0);
  const [auditStatus, setAuditStatus] = useState<{
    userId: string;
    scanning: boolean;
    result: any | null;
  } | null>(null);

  useEffect(() => {
    loadStorageData();
  }, []);


  const runFullAudit = async (userId: string, autoFix: boolean) => {
    try {
      setAuditStatus({ userId, scanning: true, result: null });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Geen actieve sessie gevonden');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/storage-audit`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, autoFix }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Audit failed');
      }

      const data = await response.json();
      setAuditStatus({ userId, scanning: false, result: data.result });

      if (autoFix && data.result.createdLogs > 0) {
        await loadStorageData();
        if (selectedUser === userId) {
          await loadUserImages(userId);
        }
      }

      return data;
    } catch (error) {
      console.error('Error running audit:', error);
      setAuditStatus({ userId, scanning: false, result: null });
      const message = error instanceof Error ? error.message : 'Kon audit niet uitvoeren';
      alert(message);
    }
  };

  const loadStorageData = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase.from('user_storage_summary').select('*');

      if (error) throw error;

      setUsers(data || []);
      const total = data?.reduce((sum, u) => sum + (u.total_bytes || 0), 0) || 0;
      setTotalStorage(total);
    } catch (error) {
      console.error('Error loading storage data:', error);
      alert('Kon storage data niet laden');
    } finally {
      setLoading(false);
    }
  };

  const loadUserImages = async (userId: string) => {
    try {
      setLoadingImages(true);

      const { data: logs, error } = await supabase
        .from('image_storage_logs')
        .select('*')
        .eq('user_id', userId)
        .eq('action', 'upload')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching logs:', error);
        throw error;
      }

      const images: ImageFile[] = logs?.map((log) => {
        const { data: urlData } = supabase.storage
          .from('blog-images')
          .getPublicUrl(log.file_path);

        return {
          path: log.file_path,
          size: log.file_size,
          uploaded_at: log.created_at,
          url: urlData.publicUrl,
        };
      }) || [];

      setUserImages(images);
    } catch (error) {
      console.error('Error loading user images:', error);
      alert('Kon afbeeldingen niet laden. Check console voor details.');
    } finally {
      setLoadingImages(false);
    }
  };

  const handleDeleteImage = async (path: string, userId: string) => {
    if (!confirm('Weet je zeker dat je deze afbeelding wilt verwijderen?')) return;

    try {
      const { error: storageError } = await supabase.storage.from('blog-images').remove([path]);

      if (storageError) throw storageError;

      const imageToDelete = userImages.find((img) => img.path === path);
      if (imageToDelete) {
        await supabase.from('image_storage_logs').insert({
          user_id: userId,
          file_path: path,
          file_size: imageToDelete.size,
          action: 'delete',
        });
      }

      setUserImages(userImages.filter((img) => img.path !== path));
      await loadStorageData();

      alert('Afbeelding verwijderd');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Kon afbeelding niet verwijderen');
    }
  };

  const filteredUsers = users
    .filter(
      (u) =>
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.company_name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aVal: number | string = 0;
      let bVal: number | string = 0;

      switch (sortBy) {
        case 'name':
          aVal = a.first_name;
          bVal = b.first_name;
          break;
        case 'files':
          aVal = a.total_files || 0;
          bVal = b.total_files || 0;
          break;
        case 'size':
          aVal = a.total_bytes || 0;
          bVal = b.total_bytes || 0;
          break;
        case 'date':
          aVal = a.last_upload_at ? new Date(a.last_upload_at).getTime() : 0;
          bVal = b.last_upload_at ? new Date(b.last_upload_at).getTime() : 0;
          break;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      return sortOrder === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

  const toggleSort = (field: 'name' | 'files' | 'size' | 'date') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner text="Storage data laden..." />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Storage Management</h1>
          <p className="mt-2 text-sm text-gray-600">Beheer en monitor gebruikers storage gebruik</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="card p-6">
            <div className="flex items-center gap-4">
              <HardDrive className="w-8 h-8 text-[#231F20]" />
              <div>
                <div className="text-sm text-gray-600">Totale Storage</div>
                <div className="text-2xl font-bold text-[#231F20]">{formatBytes(totalStorage)}</div>
              </div>
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center gap-4">
              <User className="w-8 h-8 text-[#231F20]" />
              <div>
                <div className="text-sm text-gray-600">Totaal Gebruikers</div>
                <div className="text-2xl font-bold text-[#231F20]">{users.length}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Zoek op naam, email of bedrijf..."
                className="input-field pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th
                    onClick={() => toggleSort('name')}
                    className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      Gebruiker
                      {sortBy === 'name' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </div>
                  </th>
                  <th
                    onClick={() => toggleSort('files')}
                    className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      Bestanden
                      {sortBy === 'files' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </div>
                  </th>
                  <th
                    onClick={() => toggleSort('size')}
                    className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      Storage
                      {sortBy === 'size' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </div>
                  </th>
                  <th
                    onClick={() => toggleSort('date')}
                    className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      Laatste Upload
                      {sortBy === 'date' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </div>
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Acties</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <React.Fragment key={user.user_id}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-[#231F20]">{user.first_name}</div>
                          <div className="text-sm text-gray-600">{user.email}</div>
                          <div className="text-sm text-gray-500">{user.company_name}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-700">{user.total_files || 0}</td>
                      <td className="py-3 px-4 text-gray-700 font-medium">{formatBytes(user.total_bytes || 0)}</td>
                      <td className="py-3 px-4 text-gray-600 text-sm">
                        {user.last_upload_at ? new Date(user.last_upload_at).toLocaleDateString('nl-NL') : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              if (selectedUser === user.user_id) {
                                setSelectedUser(null);
                                setUserImages([]);
                              } else {
                                setSelectedUser(user.user_id);
                                loadUserImages(user.user_id);
                              }
                            }}
                            className="text-[#231F20] hover:underline text-sm font-medium"
                          >
                            {selectedUser === user.user_id ? 'Verberg' : 'Toon afbeeldingen'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {selectedUser === user.user_id && (
                      <tr>
                        <td colSpan={5} className="p-4 bg-gray-50">
                          {auditStatus?.userId === user.user_id && auditStatus.result && (
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                                <div className="flex-1">
                                  <h4 className="font-semibold text-blue-900 mb-2">Audit Resultaat</h4>
                                  <div className="text-sm text-blue-800 space-y-1">
                                    <p>Gescande bestanden: {auditStatus.result.scannedFiles}</p>
                                    <p>Bestaande logs: {auditStatus.result.existingLogs}</p>
                                    <p>Ontbrekende logs: {auditStatus.result.missingLogs}</p>
                                    {auditStatus.result.createdLogs > 0 && (
                                      <p className="font-semibold text-green-700">
                                        ✓ {auditStatus.result.createdLogs} logs aangemaakt
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => setAuditStatus(null)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                          {loadingImages ? (
                            <div className="text-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E94E87] mx-auto"></div>
                            </div>
                          ) : userImages.length === 0 ? (
                            <div className="text-center py-8">
                              <p className="text-gray-600 mb-4">Geen afbeeldingen gevonden in logs</p>
                              <button
                                onClick={() => runFullAudit(user.user_id, false)}
                                disabled={auditStatus?.scanning && auditStatus?.userId === user.user_id}
                                className="btn-secondary text-sm"
                              >
                                {auditStatus?.scanning && auditStatus?.userId === user.user_id ? (
                                  <>Scannen...</>
                                ) : (
                                  <>Scan storage bucket</>
                                )}
                              </button>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                              {userImages.map((img) => (
                                <div key={img.path} className="relative group">
                                  <img
                                    src={img.url}
                                    alt={img.path}
                                    className="w-full h-32 object-cover rounded-lg border border-gray-200"
                                    onError={(e) => {
                                      e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="128"%3E%3Crect fill="%23e5e7eb" width="200" height="128"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="12"%3ENiet beschikbaar%3C/text%3E%3C/svg%3E';
                                    }}
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center">
                                    <button
                                      onClick={() => handleDeleteImage(img.path, user.user_id)}
                                      className="opacity-0 group-hover:opacity-100 bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition-all"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <div className="mt-2 text-xs text-gray-600">
                                    <div className="truncate">{img.path.split('/').pop()}</div>
                                    <div>{formatBytes(img.size)}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-gray-600">Geen gebruikers gevonden</div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}